import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';

const slides = [
  {
    headline: 'Buy & Sell Social Media Accounts Instantly',
    subheadline: 'The safest marketplace for YouTube, Instagram, TikTok, and more.',
    cta: 'Browse Marketplace',
    ctaLink: '/marketplace',
    illustration: (
      <div style={{
        position: 'relative',
        width: 220,
        height: 220,
        background: '#E0E7FF',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <img src="/images/categories/youtube.svg" alt="YouTube" style={{ position: 'absolute', top: 10, left: 85, width: 50, height: 50 }} />
        <img src="/images/categories/instagram.svg" alt="Instagram" style={{ position: 'absolute', right: 10, top: 85, width: 50, height: 50 }} />
        <img src="/images/categories/tiktok.svg" alt="TikTok" style={{ position: 'absolute', bottom: 10, left: 85, width: 50, height: 50 }} />
        <img src="/images/categories/facebook.svg" alt="Facebook" style={{ position: 'absolute', left: 10, top: 85, width: 50, height: 50 }} />
      </div>
    ),
    bg: 'from-yellow-50 to-white',
  },
  {
    headline: 'Secure Transactions, Fast Transfers',
    subheadline: 'Your safety is our priority. Escrow and 24/7 support included.',
    cta: 'Learn More',
    ctaLink: '/escrow',
    illustration: (
      <div style={{ position: 'relative', width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="180" height="180" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="60" cy="60" r="56" fill="#F0FDF4" stroke="#34D399" strokeWidth="8"/>
          <path d="M60 30L90 45V65C90 85 60 95 60 95C60 95 30 85 30 65V45L60 30Z" fill="#34D399" stroke="#059669" strokeWidth="4"/>
          <path d="M50 65L58 73L72 55" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <svg width="60" height="60" viewBox="0 0 40 40" fill="none" style={{position: 'absolute', right: 20, bottom: 20}} xmlns="http://www.w3.org/2000/svg">
          <polygon points="20,5 25,20 15,20 20,35" fill="#FBBF24" stroke="#F59E42" strokeWidth="2"/>
        </svg>
      </div>
    ),
    bg: 'from-blue-50 to-white',
  },
  {
    headline: 'Grow Your Business with Verified Accounts',
    subheadline: 'Handpicked, high-quality accounts for every need.',
    cta: 'Get Started',
    ctaLink: '/signup',
    illustration: (
      <div style={{ position: 'relative', width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="180" height="180" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="60" cy="60" r="56" fill="#FEF9C3" stroke="#FBBF24" strokeWidth="8"/>
          {/* Bar Chart */}
          <rect x="35" y="80" width="12" height="25" rx="4" fill="#FBBF24"/>
          <rect x="55" y="65" width="12" height="40" rx="4" fill="#F59E42"/>
          <rect x="75" y="50" width="12" height="55" rx="4" fill="#FDE68A"/>
          {/* Upward Arrow */}
          <defs>
            <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L8,4 L0,8 L2,4 Z" fill="#F59E42" />
            </marker>
          </defs>
          <path d="M40 90 L60 60 L80 70" stroke="#F59E42" strokeWidth="4" fill="none" markerEnd="url(#arrowhead)" />
          {/* Verified Badge */}
          <circle cx="90" cy="35" r="18" fill="#34D399" stroke="#059669" strokeWidth="3"/>
          <path d="M84 35l6 6 10-12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    ),
    bg: 'from-green-50 to-white',
  },
  {
    headline: 'What Our Users Say About YTProud',
    subheadline: 'Safe and easy YouTube channel buying. Highly recommended! – Alex G.',
    cta: 'See More Reviews',
    ctaLink: 'https://www.trustpilot.com/review/ytproud.com',
    isExternal: true,
    illustration: (
      // Trustpilot-style Star with rating
      <svg width="220" height="220" viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="110" cy="110" r="100" fill="#00002d"/>
        <polygon points="110,50 127,95 175,95 136,122 150,170 110,140 70,170 84,122 45,95 93,95" fill="#00b77b"/>
        <rect x="60" y="150" width="100" height="30" rx="15" fill="#fff"/>
        <text x="110" y="170" textAnchor="middle" fill="#000" fontSize="20" fontWeight="bold" dominantBaseline="middle" alignmentBaseline="middle">★ 4.9/5</text>
      </svg>
    ),
    bg: 'from-yellow-50 to-white',
  },
];

const ModernHeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const { isDarkMode } = useDarkMode();

  return (
    <div className="relative w-full mx-auto rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg sm:shadow-xl dark:shadow-2xl dark:shadow-black/30 bg-white dark:bg-[#1a1a1a] px-0" style={{ maxWidth: '1650px' }}>
      {/* Mobile Design - Single Row Layout */}
      <div className="block lg:hidden">
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-4 min-h-[160px] transition-all duration-500">
          <div className="flex flex-col justify-center items-center text-center">
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-2 leading-tight">
              {slides[current].headline}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4 max-w-2xl">{slides[current].subheadline}</p>
            {slides[current].isExternal ? (
              <a
                href={slides[current].ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 rounded-full bg-red-600 text-white font-bold shadow hover:bg-red-700 transition text-sm"
              >
                {slides[current].cta}
              </a>
            ) : (
            <Link
              to={slides[current].ctaLink}
              className="inline-block px-5 py-2.5 rounded-full bg-red-600 text-white font-bold shadow hover:bg-red-700 transition text-sm"
            >
              {slides[current].cta}
            </Link>
            )}
          </div>
          
          {/* Mobile Social Media Icons Row */}
          <div className="flex justify-center items-center gap-3 mt-2 mb-6">
            <div className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
              <img src="/images/categories/youtube.svg" alt="YouTube" className="w-4 h-4" />
            </div>
            <div className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
              <img src="/images/categories/facebook.svg" alt="Facebook" className="w-4 h-4" />
            </div>
            <div className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
              <img src="/images/categories/instagram.svg" alt="Instagram" className="w-4 h-4" />
            </div>
            <div className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
              <img src="/images/categories/tiktok.svg" alt="TikTok" className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Design - Original Layout */}
      <div className="hidden lg:flex flex-col lg:flex-row items-center justify-between gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 min-h-[200px] sm:min-h-[240px] lg:min-h-[280px] transition-all duration-500">
        <div className="flex-1 flex flex-col justify-center items-center lg:items-start text-center lg:text-left">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-gray-900 dark:text-white mb-3 sm:mb-4 leading-tight">
            {slides[current].headline}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-2xl">{slides[current].subheadline}</p>
          {slides[current].isExternal ? (
            <a
              href={slides[current].ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full bg-red-600 text-white font-bold shadow hover:bg-red-700 transition text-base sm:text-lg"
            >
              {slides[current].cta}
            </a>
          ) : (
          <Link
            to={slides[current].ctaLink}
            className="inline-block px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full bg-red-600 text-white font-bold shadow hover:bg-red-700 transition text-base sm:text-lg"
          >
            {slides[current].cta}
          </Link>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full flex justify-center">
            <div className="scale-75 sm:scale-90 lg:scale-100">
              {slides[current].illustration}
            </div>
          </div>
        </div>
      </div>
      
      {/* Slider Controls */}
      <div className="absolute bottom-2 sm:bottom-3 lg:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition border-2 border-red-200 dark:border-red-600 ${current === idx ? 'bg-red-500 border-red-500 dark:bg-red-500 dark:border-red-500' : 'bg-white dark:bg-gray-700'}`}
            onClick={() => setCurrent(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ModernHeroSlider; 